import { useState, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import {
  updateChartFilterGroupsFilters,
  updateChartFilterGroupsCollapsed,
} from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { AlertColor } from '@mui/material/Alert';
import { groupProp } from '../../../redux/ChartFilterGroup/ChartFilterGroupInterface';
import { Box, Divider } from '@mui/material';
import Logger from '../../../Logger';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import LoadingPopover from '../../CommonFunctions/PopOverComponents/LoadingPopover';
import { messages } from '../../CommonFunctions/aliases';
import GroupHeader from './GroupHeader';
import DashboardFilterGroupPopover from './DashboardFilterGroupPopover';
import SyncFilterMenu from './SyncFilterMenu';
import FilterGroupContent from './FilterGroupContent';
import ShortUniqueId from 'short-unique-id';
import { SetModifyOptions } from '../../../redux/ChartPoperties/ChartControlsActions';

interface Props {
  propKey: string;
  group: groupProp;
  fromDashboard: boolean;
  reportFilterMenu?: boolean;
  setReportFilterMenu?: (value: boolean) => void;
  manualReportFilterMenu?: boolean;
  setManualReportFilterMenu?: (value: boolean) => void;

  color?: string;
  backgroundColor?: string;
}

/**
 * rendered by ChartFilterGroupList.tsx
 * @returns multiple components in a filter group.
 * A header which contains gorup name with edit functionality and two icons.
 * FilterGroupContent that shows content of group
 * DashboardFilterGroupPopover that contains a popover component on clicking more icon in group header.
 * SyncFilterMenu
 */
const ChartFilterGroups = ({
  propKey,
  group,
  fromDashboard,
  reportFilterMenu,
  setReportFilterMenu,
  manualReportFilterMenu,
  setManualReportFilterMenu,
  color,
  backgroundColor,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [severity, setSeverity] = useState<AlertColor>('success');
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [testMessage, setTestMessage] = useState<string>('');
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [anchorElSyncFilter, setAnchorElSyncFilter] = useState<HTMLButtonElement | null>(null);
  const [showBorder, setShowBorder] = useState(false);

  const { tabTileProps, chartProp, chartGroup, dashBoardGroup, tileState, tabState, calculations } =
    useSelector((state: RootState) => ({
      tabTileProps: state.tabTileProps,
      chartProp: state.chartProperties,
      chartGroup: state.chartFilterGroup,
      dashBoardGroup: state.dashBoardFilterGroup,
      tileState: state.tileState,
      tabState: state.tabState,
      calculations: state.calculations,
    }));

  const dispatch = useDispatch();

  let selectedDatasetID = '';
  let selectedGroupTabTilesList: any = [];
  let dashboardTabTileList: any = [];
  let tilesForSelectedTab = tileState.tileList[tabTileProps.selectedTabId];

  let link: string = '';

  if (window.location.href.includes('dashboard')) {
    let url = window.location.href.split('/').slice(-1);
    link = url.toString();
  }

  const [{ isOver }, drop] = useDrop({
    accept: ['card', 'calculation'],
    hover: (item: any, monitor) => {
      if (monitor.isOver({ shallow: true }) && !showBorder) {
        setShowBorder(true);
      }
    },
    drop: (item: any) => {
      setShowBorder(false);
      handleDrop(item, group);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (!isOver) {
      setShowBorder(false);
    }
  }, [isOver]);

  if (!fromDashboard) {
    selectedDatasetID = chartProp.properties[propKey].selectedDs.id;
  } else {
    selectedGroupTabTilesList = dashBoardGroup.filterGroupTabTiles[group.id];

    [...tilesForSelectedTab].forEach((tile: any) => {
      if (tabState.tabs[tabTileProps.selectedTabId].tilesInDashboard.includes(tile)) {
        dashboardTabTileList.push({
          name: tileState.tiles[tile].tileName,
          id: tile,
          disabled:
            chartGroup.groups[group.id].dataSetId !== chartProp.properties[tile].selectedDs.id,
        });
      }
    });
  }

  const uIdGenerator = () => {
    const uId = new ShortUniqueId({ length: 4 });
    return uId();
  };

  // DropZoneDropItem
  const handleDrop = (item: any, group: groupProp) => {
    const savedAggregatedCalculations = calculations.savedCalculations.filter(
      (calc: any) => calc.isAggregated,
    );
    if (
      savedAggregatedCalculations.find(
        (calc: any) => calc.calculationInfo.calculatedFieldName === item.fieldData.fieldname,
      )
    ) {
      setSeverity('warning');
      setOpenAlert(true);
      setTestMessage("Can't drop aggregated calculations here");
      return;
    }
    if (group.syncFilter === true && item.boxUUID) {
      setSeverity('warning');
      setOpenAlert(true);
      setTestMessage('Sync filter is not available for calculated field');
      return;
    }
    if (item.bIndex === 99) {
      if (selectedDatasetID !== group.dsIdForGroup) {
        Logger(
          'info',
          'datasets',
          `selectedDatasetID:${selectedDatasetID},group.dataSetId:${group.dsIdForGroup}`,
        );
        setSeverity('warning');
        setOpenAlert(true);
        setTestMessage(messages.playbook.FilterGroupBelongsToOtherDataset);
        return;
      }
      const uID = uIdGenerator();
      var fieldData = item.fieldData;
      fieldData.uId = uID;
      fieldData.isCalculatedField = item.boxUUID ? true : false;
      fieldData.SavedCalculationUUID = item.boxUUID ?? null;
      if (item.boxUUID) fieldData.displayName2 = fieldData.displayname;
      dispatch(SetModifyOptions(propKey, 'filter_group', `${group.id}_${uID}`));
      dispatch(updateChartFilterGroupsFilters(selectedDatasetID, group.id, fieldData));

      // if (canChangeDatasetForGroup)
      //   dispatch(changeDatasetOfFilterGroup(group.id, selectedDatasetID));
      dispatch(updateChartFilterGroupsCollapsed(group.id, false));
      return;
    }
    if (item.filterGroupId !== group.id) {
      setSeverity('warning');
      setOpenAlert(true);
      setTestMessage(messages.playbook.FilterGroupBelongsToOtherDataset);
      return;
    }
  };

  let groupsStyle: any = {
    textAlign: 'left',
    paddingBottom: '2rem',
  };

  if (!group.isCollapsed && group.filters && group.filters.length === 0) {
    groupsStyle['minHeight'] = '100px';
  }

  const [selectedFilterGroupsNamesInDashBoard, setSelectedFilterGroupsNamesInDashBoard] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (group) {
      setSelectedFilterGroupsNamesInDashBoard([
        ...selectedFilterGroupsNamesInDashBoard,
        group.name,
      ]);
    }
  }, [group]);

  const isPresent = tabTileProps.dashMode === 'Present';
  const isEdit = tabTileProps.dashMode === 'Edit';
  const isGroupVisible = (isPresent && reportFilterMenu) || (isEdit && manualReportFilterMenu);

  return (
    <>
      <Box
        data-testid="chart-filter-groups-box"
        ref={drop}
        style={{
          ...groupsStyle,
          border: '1px solid transparent',
          display: isGroupVisible ? 'block' : 'none',
        }}
        className={`dropzone ${showBorder ? 'dropzone-highlighted' : ''}`}
      >
        {tabTileProps.dashMode === 'Present' && reportFilterMenu && (
          <GroupHeader
            group={group}
            fromDashboard={fromDashboard}
            setSeverity={setSeverity}
            setOpenAlert={setOpenAlert}
            setTestMessage={setTestMessage}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            setLoading={setLoading}
            setShowPopover={setShowPopover}
            setAnchorElSyncFilter={setAnchorElSyncFilter}
            color={color}
          />
        )}

        {tabTileProps.dashMode === 'Edit' && manualReportFilterMenu && (
          <GroupHeader
            group={group}
            fromDashboard={fromDashboard}
            setSeverity={setSeverity}
            setOpenAlert={setOpenAlert}
            setTestMessage={setTestMessage}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            setLoading={setLoading}
            setShowPopover={setShowPopover}
            setAnchorElSyncFilter={setAnchorElSyncFilter}
            color={color}
          />
        )}

        {tabTileProps.dashMode === 'Present' && reportFilterMenu && (
          <FilterGroupContent
            propKey={propKey}
            group={group}
            fromDashboard={fromDashboard}
            link={link}
            color={color}
            backgroundColor={backgroundColor}
          />
        )}

        {tabTileProps.dashMode === 'Edit' && manualReportFilterMenu && (
          <FilterGroupContent
            propKey={propKey}
            group={group}
            fromDashboard={fromDashboard}
            link={link}
            color={color}
            backgroundColor={backgroundColor}
          />
        )}

        <NotificationDialog
          onCloseAlert={() => {
            setOpenAlert(false);
            setTestMessage('');
          }}
          severity={severity}
          testMessage={testMessage}
          openAlert={openAlert}
          splitLines
        />

        <DashboardFilterGroupPopover
          group={group}
          setSeverity={setSeverity}
          setOpenAlert={setOpenAlert}
          setTestMessage={setTestMessage}
          showPopover={showPopover}
          selectedGroupTabTilesList={selectedGroupTabTilesList}
          setAnchorElSyncFilter={setAnchorElSyncFilter}
          dashboardTabTileList={dashboardTabTileList}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          setShowPopover={setShowPopover}
          link={link}
          setLoading={setLoading}
        />

        <SyncFilterMenu
          group={group}
          anchorElSyncFilter={anchorElSyncFilter}
          setAnchorElSyncFilter={setAnchorElSyncFilter}
          setSeverity={setSeverity}
          setOpenAlert={setOpenAlert}
          setTestMessage={setTestMessage}
          link={link}
          setLoading={setLoading}
        />

        {loading ? <LoadingPopover /> : null}
      </Box>
      {isGroupVisible && (
        <Divider
          sx={{
            height: '2px',
            backgroundColor: 'lightgray',
            border: 'none',
            margin: '3px 0 3px 0',
          }}
        />
      )}
    </>
  );
};

export default ChartFilterGroups;
